import { useMemo } from 'react';
import { Button, Dialog } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useWarehouseLayout } from '../model/warehouse.queries.js';
import type { LayoutCell, LayoutRack } from '../api/warehouse.api.js';
import { CellPanel } from './CellPanel.js';

export interface CellCardDialogProps {
  warehouseId: string;
  /** Ochilgan katak. `null` — oyna yopiq. */
  cellId: string | null;
  onClose: () => void;
  /**
   * "Shu katakni tanlash". Berilmasa tugma chizilmaydi — oyna faqat
   * ko'rsatish uchun ochilgan bo'ladi.
   */
  onUse?: (code: string) => void;
}

/**
 * KATAK KARTOCHKASI — turgan sahifadan chiqmasdan.
 *
 * Kirim to'ldirayotgan odamga "u yerda hozir nima turibdi?" degan savol
 * tez-tez tushadi. Ombor ekraniga o'tib, katakni topib, keyin qaytib
 * kelish esa yarim to'ldirilgan hujjatni tashlab ketish degani edi.
 *
 * FAQAT O'QISH uchun: `onSave` va `onAllocate` berilmagan, ya'ni tahrir
 * bo'limi umuman chizilmaydi. Kirim paytida javon o'lchamini o'zgartirish
 * — boshqa ish va boshqa ruxsat.
 *
 * Ma'lumot ombor DARAXTIDAN olinadi, alohida so'rovsiz: daraxt bu
 * sahifada allaqachon olingan (ajratilgan kataklar ro'yxati uchun) va
 * `react-query` uni bir marta yuklaydi.
 */
export const CellCardDialog = ({ warehouseId, cellId, onClose, onUse }: CellCardDialogProps) => {
  const layout = useWarehouseLayout(cellId === null || warehouseId === '' ? null : warehouseId);

  const found = useMemo<{ cell: LayoutCell; rack: LayoutRack } | null>(() => {
    if (cellId === null) return null;
    for (const zone of layout.data?.zones ?? []) {
      for (const rack of zone.racks) {
        const cell = rack.cells.find((candidate) => candidate.id === cellId);
        if (cell !== undefined) return { cell, rack };
      }
    }
    return null;
  }, [cellId, layout.data]);

  if (found === null) return null;

  const rackName =
    found.rack.name === null || found.rack.name === ''
      ? found.rack.code
      : `${found.rack.code} — ${found.rack.name}`;

  return (
    <Dialog
      open
      onClose={onClose}
      title={found.cell.code}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('stock.close')}
          </Button>
          {onUse === undefined ? null : (
            <Button onClick={() => onUse(found.cell.code)}>{t('stock.cellSuggestUse')}</Button>
          )}
        </>
      }
    >
      <CellPanel variant="dialog" cell={found.cell} rackName={rackName} onClose={onClose} />
    </Dialog>
  );
};
